import { devtools } from 'zustand/middleware';
import { create } from 'store/create';
import { cellRefMatches, reviewItemMatchesFilter } from './types';
import type { ReviewItem, ReviewProvenanceFilter, ReviewStatus, ReviewStatusFilter, ReviewStore } from './types';

/** The security, metric and cell filters with nothing narrowed. */
const emptyReviewFilter = () => ({
  filterSecurities: new Set<string>(),
  filterMetrics: new Set<string>(),
  filterCellIds: new Set<string>(),
});

/** The filter the review panel opens with: every status, AI cards, nothing narrowed. */
export const defaultReviewFilter = () => ({
  ...emptyReviewFilter(),
  filterStatus: 'all' as ReviewStatusFilter,
  filterProvenance: 'ai' as ReviewProvenanceFilter,
});

/** The default filter with the pane back to its automatic visibility. */
const defaultReviewView = () => ({ ...defaultReviewFilter(), paneCollapsed: null });

/** Return a copy of `set` with `value` toggled in: removed if present, else added. */
const withToggled = (set: Set<string>, value: string): Set<string> => {
  const next = new Set(set);
  if (!next.delete(value)) {
    next.add(value);
  }
  return next;
};

/** Add `step` holds to each card, dropping a card once it has none. */
const withHolds = (busy: Map<string, number>, ids: string[], step: number): Map<string, number> => {
  const next = new Map(busy);
  ids.forEach(id => {
    const holds = (next.get(id) ?? 0) + step;
    if (holds > 0) {
      next.set(id, holds);
    } else {
      next.delete(id);
    }
  });
  return next;
};

const useReviewStore = create<ReviewStore>()(
  devtools(
    set => ({
      reviewItems: [],
      selectedCardIds: new Set<string>(),
      busyCardIds: new Map<string, number>(),
      ...defaultReviewView(),
      hoveredReviewCell: null,
      selectedReviewCell: null,
      drawerOpen: false,
      mountedReviewPanels: 0,
      showAiColumnControls: false,
      reviewerMode: false,

      setReviewItems: items => set(() => ({ reviewItems: items }), false, 'setReviewItems'),

      toggleCardSelection: id =>
        set(state => ({ selectedCardIds: withToggled(state.selectedCardIds, id) }), false, 'toggleCardSelection'),

      selectCards: ids => set(() => ({ selectedCardIds: new Set(ids) }), false, 'selectCards'),

      clearSelection: () => set(() => ({ selectedCardIds: new Set<string>() }), false, 'clearSelection'),

      holdCards: ids => {
        set(s => ({ busyCardIds: withHolds(s.busyCardIds, ids, 1) }), false, 'holdCards');
        return () => set(s => ({ busyCardIds: withHolds(s.busyCardIds, ids, -1) }), false, 'releaseCards');
      },

      toggleFilterSecurity: label =>
        set(state => ({ filterSecurities: withToggled(state.filterSecurities, label) }), false, 'toggleFilterSecurity'),

      toggleFilterMetric: label =>
        set(state => ({ filterMetrics: withToggled(state.filterMetrics, label) }), false, 'toggleFilterMetric'),

      clearReviewFilter: () => set(emptyReviewFilter, false, 'clearReviewFilter'),

      resetReviewFilter: () => set(defaultReviewView, false, 'resetReviewFilter'),

      setFilterCellIds: ids => set(() => ({ filterCellIds: new Set(ids) }), false, 'setFilterCellIds'),

      // A different view must not keep hidden cards selected for a bulk action.
      setFilterStatus: status =>
        set(() => ({ filterStatus: status, selectedCardIds: new Set<string>() }), false, 'setFilterStatus'),

      setFilterProvenance: provenance =>
        set(() => ({ filterProvenance: provenance, selectedCardIds: new Set<string>() }), false, 'setFilterProvenance'),

      removeReviewItem: id =>
        set(
          state => {
            const next = new Set(state.selectedCardIds);
            next.delete(id);
            return {
              reviewItems: state.reviewItems.filter(i => i.id !== id),
              selectedCardIds: next,
            };
          },
          false,
          'removeReviewItem'
        ),

      setReviewItemStatus: (id, status, valueKept) =>
        set(
          state => {
            const next = new Set(state.selectedCardIds);
            next.delete(id);
            // Settling an item also drops any grid outline pointing at its cell
            // (hovered/selected review cell), so a dismissed/accepted cell never
            // keeps the purple review ring (SCAL-8976 follow-up). A card the user
            // re-activates from the Reviewed section re-selects explicitly.
            const item = status !== 'pending' ? state.reviewItems.find(i => i.id === id) : undefined;
            const ref = item ? { securityId: item.securityId, fieldAlias: item.fieldAlias } : null;
            return {
              // valueKept (SCAL-9262) is stamped only by the reopen flow; every
              // other transition passes undefined, clearing any stale flag.
              reviewItems: state.reviewItems.map(i => (i.id === id ? { ...i, status, valueKept } : i)),
              selectedCardIds: next,
              ...(ref && cellRefMatches(state.hoveredReviewCell, ref) ? { hoveredReviewCell: null } : {}),
              ...(ref && cellRefMatches(state.selectedReviewCell, ref) ? { selectedReviewCell: null } : {}),
            };
          },
          false,
          'setReviewItemStatus'
        ),

      setHoveredReviewCell: cell => set(() => ({ hoveredReviewCell: cell }), false, 'setHoveredReviewCell'),

      setSelectedReviewCell: cell => set(() => ({ selectedReviewCell: cell }), false, 'setSelectedReviewCell'),

      mountReviewPanel: () => {
        set(s => ({ mountedReviewPanels: s.mountedReviewPanels + 1, drawerOpen: true }), false, 'mountReviewPanel');
        return () =>
          set(
            s =>
              s.mountedReviewPanels > 1
                ? { mountedReviewPanels: s.mountedReviewPanels - 1 }
                : { mountedReviewPanels: 0, drawerOpen: false, selectedReviewCell: null, hoveredReviewCell: null },
            false,
            'unmountReviewPanel'
          );
      },

      setPaneCollapsed: collapsed => set(() => ({ paneCollapsed: collapsed }), false, 'setPaneCollapsed'),

      setShowAiColumnControls: show => set(() => ({ showAiColumnControls: show }), false, 'setShowAiColumnControls'),

      setReviewerMode: on => set(() => ({ reviewerMode: on }), false, 'setReviewerMode'),

      resetReviewSession: () =>
        set(
          () => ({
            ...defaultReviewView(),
            reviewItems: [],
            selectedCardIds: new Set<string>(),
            reviewerMode: false,
          }),
          false,
          'resetReviewSession'
        ),
    }),
    { name: 'ReviewStore' }
  )
);

/**
 * Subscribe to whether one specific cell should reveal its AI merge text / outline:
 * true when (securityId, fieldAlias) matches the hovered OR the selected review cell.
 * The selector returns a plain boolean, so a cell's viewer only re-renders when its
 * own match flips - hovering a card does not re-render the whole grid.
 */
export const useCellShowsMerge = (securityId?: number, fieldAlias?: string): boolean =>
  useReviewStore(s => {
    if (securityId == null || !fieldAlias) {
      return false;
    }
    const target = { securityId, fieldAlias };
    return cellRefMatches(s.hoveredReviewCell, target) || cellRefMatches(s.selectedReviewCell, target);
  });

/**
 * Value-review status for one cell, or undefined when its card reviews only
 * citations. Grid viewers still pick up value settles immediately, but a fresh
 * citation must not override the existing value's local metadata or preview.
 */
export const useReviewItemStatus = (securityId?: number, fieldAlias?: string): ReviewStatus | undefined =>
  useReviewStore(s =>
    securityId == null || !fieldAlias
      ? undefined
      : s.reviewItems.find(i => i.id === `${securityId}:${fieldAlias}` && i.canSettle)?.status
  );

/** Whether one cell's card has a review decision still saving, so the cell offers none of its own. */
export const useReviewItemBusy = (securityId?: number, fieldAlias?: string): boolean =>
  useReviewStore(s => securityId != null && !!fieldAlias && s.busyCardIds.has(`${securityId}:${fieldAlias}`));

/** Whether any card of one security has a review decision still saving. */
export const useSecurityReviewBusy = (securityId?: number | string | null): boolean =>
  useReviewStore(s => securityId != null && [...s.busyCardIds.keys()].some(id => id.startsWith(`${securityId}:`)));

/** Number of items still pending review; reviewed cards are not counted. */
export const usePendingReviewCount = (): number =>
  useReviewStore(s => s.reviewItems.reduce((n, i) => (i.status === 'pending' ? n + 1 : n), 0));

/**
 * Whether the review drawer is open. Pending AI cells subscribe to this so they paint
 * AI-purple by default while the drawer is open and revert when it closes (SCAL-9150).
 * Returns a plain boolean, so a cell's viewer re-renders only when the flag flips.
 */
export const useReviewDrawerOpen = (): boolean => useReviewStore(s => s.drawerOpen);

const { filterSecurities, filterMetrics, filterStatus, filterProvenance, filterCellIds } = defaultReviewFilter();
/** Whether a card shows in the panel before the user changes its filter. */
const inDefaultView = (i: ReviewItem): boolean =>
  reviewItemMatchesFilter(i, filterSecurities, filterMetrics, filterStatus, filterProvenance, filterCellIds);

/** Keep an explicit show/hide choice; otherwise show the panel only when its default view has a card. */
export const useReviewPaneCollapsed = (): boolean =>
  useReviewStore(s => s.paneCollapsed ?? !s.reviewItems.some(inDefaultView));

/**
 * Whether the current user gets the AI review experience at all (SCAL-9335).
 * False (the default) until CapTableWidgetDrawer stamps the analyst-side role
 * check in - grid viewers subscribe to this and render the plain cap table
 * (normal blue citations, no AI hue, no settle affordances) while it is false.
 */
export const useReviewerMode = (): boolean => useReviewStore(s => s.reviewerMode);

export default useReviewStore;
